import React, { useRef } from 'react';
import { Camera, ImagePlus, X, Eye, EyeOff } from 'lucide-react';

export interface PendingPhoto {
  file: File;
  previewUrl: string;
  dataUrl: string;
  /**
   * Publikus legyen-e a kép, ha az állat elmentődik.
   *
   * A feltöltés MINDIG privátba történik; ez csak szándék, amit a mentés
   * után érvényesítünk. Alapértéke `false` — ugyanaz a szabály, mint az
   * adatlapon: ami nem kap külön döntést, az privát marad.
   */
  isPublic: boolean;
}

interface PhotoUploadFieldProps {
  photos: PendingPhoto[];
  onChange: (photos: PendingPhoto[]) => void;
  /** Already-uploaded photo URLs (e.g. when editing an existing animal) — shown first, ahead of any newly-added ones. */
  existingUrls?: string[];
  /** Called when the staff member removes one of the already-uploaded photos. */
  onRemoveExisting?: (url: string) => void;
  /** Shows a red "required" hint when true and no photos (existing + new) are present. */
  required?: boolean;
}

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/**
 * Több-fájlos fotó feltöltő mező, élő thumbnail rácscsal. Mobilon a
 * `capture="environment"` közvetlenül a hátsó kamerát nyitja meg — a
 * bekerülés jellemzően telefonról történik. `existingUrls` esetén a már
 * feltöltött képek is megjelennek (eltávolíthatóan) az újonnan
 * kiválasztottak mellett — ugyanez a komponens szolgálja ki a bekerülési
 * varázslót (csak új fotók) és az állat adatlap szerkesztését (meglévő +
 * új fotók vegyesen).
 */
export const PhotoUploadField: React.FC<PhotoUploadFieldProps> = ({
  photos, onChange, existingUrls = [], onRemoveExisting, required = true,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const newPhotos = await Promise.all(
      Array.from(fileList).map(async file => ({
        file,
        previewUrl: URL.createObjectURL(file),
        dataUrl: await readAsDataUrl(file),
        isPublic: false,
      }))
    );
    onChange([...photos, ...newPhotos]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const togglePublic = (index: number) => {
    onChange(photos.map((p, i) => (i === index ? { ...p, isPublic: !p.isPublic } : p)));
  };

  const removePhoto = (index: number) => {
    const removed = photos[index];
    if (removed) URL.revokeObjectURL(removed.previewUrl);
    onChange(photos.filter((_, i) => i !== index));
  };

  const isEmpty = existingUrls.length === 0 && photos.length === 0;

  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {existingUrls.map((url, i) => (
          <div key={url} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group">
            <img src={url} alt={`Fotó ${i + 1}`} className="w-full h-full object-cover" />
            {i === 0 && (
              <span className="absolute top-1 left-1 bg-teal-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                Borítókép
              </span>
            )}
            {onRemoveExisting && (
              <button
                type="button"
                onClick={() => onRemoveExisting(url)}
                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-90 hover:bg-red-600 transition-colors"
                aria-label="Fotó eltávolítása"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}

        {photos.map((p, i) => (
          <div key={p.previewUrl} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group">
            <img src={p.previewUrl} alt={`Fotó ${existingUrls.length + i + 1}`} className="w-full h-full object-cover" />
            {existingUrls.length === 0 && i === 0 && (
              <span className="absolute top-1 left-1 bg-teal-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                Borítókép
              </span>
            )}
            <button
              type="button"
              onClick={() => removePhoto(i)}
              className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-90 hover:bg-red-600 transition-colors"
              aria-label="Fotó eltávolítása"
            >
              <X className="w-3.5 h-3.5" />
            </button>

            {/* Láthatóság már itt is állítható. A kép a mentéskor akkor is
                publikussá tehető, ha az állat maga még nem jelenik meg a
                platformon (pl. `Orvosi` státusz) — a kettő független: a
                státusz azt dönti el, listázzuk-e az állatot, a láthatóság
                azt, hogy melyik képét mutathatjuk meg. */}
            <button
              type="button"
              onClick={() => togglePublic(i)}
              className={`absolute bottom-1 left-1 flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded transition-colors ${
                p.isPublic ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-800/80 text-white hover:bg-gray-900'
              }`}
              aria-label={`Láthatóság: jelenleg ${p.isPublic ? 'publikus' : 'privát'} — kattints a váltáshoz`}
            >
              {p.isPublic ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              {p.isPublic ? 'Publikus' : 'Privát'}
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 text-xs font-medium transition-colors ${
            isEmpty && required
              ? 'border-red-300 bg-red-50 text-red-600 hover:bg-red-100'
              : 'border-gray-300 text-gray-500 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50'
          }`}
        >
          {isEmpty ? <Camera className="w-6 h-6" /> : <ImagePlus className="w-6 h-6" />}
          {isEmpty ? 'Fotó hozzáadása' : 'Több fotó'}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={e => handleFiles(e.target.files)}
      />

      {required && isEmpty && (
        <p className="text-xs text-red-600 mt-2">Legalább 1 fotó feltöltése kötelező.</p>
      )}

      {photos.length > 0 && (
        <p className="text-[11px] text-gray-400 mt-2">
          Minden kép alapértelmezetten privát; a jelvényre kattintva teheted publikussá.
          A hosszabbik él automatikusan 720 képpontra méreteződik.
        </p>
      )}
    </div>
  );
};

export default PhotoUploadField;
